import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  Divider,
  TableSortLabel,
  TablePagination,
  Avatar,
  ListItem,
  ListItemIcon,
  ListItemText,
  List
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  Delete,
  AttachMoney,
  TrendingUp,
  Receipt,
  Assessment,
  Search,
  FileDownload,
  Payment,
  FilterList,
  Person,
  Phone,
  Email,
  CalendarToday,
  AccountBalance,
  CreditCard,
  Money,
  PhoneAndroid,
  CheckCircle,
  Schedule,
  BusinessCenter
} from '@mui/icons-material';
import { useData } from '../contexts/DataContext';
import { formatCurrency } from '../utils/currency';

const SalesManagement = () => {
  const { sales, batches, addSale } = useData();
  const [addSaleOpen, setAddSaleOpen] = useState(false);
  const [viewSaleOpen, setViewSaleOpen] = useState(false);
  const [editSaleOpen, setEditSaleOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleData, setSaleData] = useState({
    batchId: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    quantityToSell: '',
    pricePerBird: '',
    saleDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    paymentStatus: 'paid',
    cashReceivedBy: '',
    invoiceNumber: '',
    notes: ''
  });
  const [error, setError] = useState('');
  
  // Filtering and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Generate invoice number
  useEffect(() => {
    if (addSaleOpen && !saleData.invoiceNumber) {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
      const timeStr = today.getTime().toString().slice(-6);
      setSaleData(prev => ({ 
        ...prev, 
        invoiceNumber: `INV-${dateStr}-${timeStr}` 
      }));
    }
  }, [addSaleOpen]);

  // Calculate sales statistics
  const salesStats = {
    totalSales: sales.length,
    totalRevenue: sales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0),
    pendingPayments: sales.filter(sale => sale.payment_status === 'pending').length,
    thisMonthSales: sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      const now = new Date();
      return saleDate.getMonth() === now.getMonth() && saleDate.getFullYear() === now.getFullYear();
    }).length
  };

  const handleAddSale = async () => {
    try {
      if (!saleData.batchId || !saleData.customerName || !saleData.quantityToSell || !saleData.pricePerBird) {
        setError('Please fill in all required fields');
        return;
      }

      if (saleData.paymentMethod === 'cash' && !saleData.cashReceivedBy) {
        setError('Please specify who received the cash payment');
        return;
      }

      const totalAmount = parseFloat(saleData.quantityToSell) * parseFloat(saleData.pricePerBird);
      const newSale = {
        batch_id: saleData.batchId,
        sale_type: 'chickens',
        quantity_sold: parseInt(saleData.quantityToSell),
        price_per_unit: parseFloat(saleData.pricePerBird),
        total_amount: totalAmount,
        buyer_name: saleData.customerName,
        buyer_contact: saleData.customerPhone,
        sale_date: saleData.saleDate,
        payment_status: saleData.paymentStatus,
        payment_method: saleData.paymentMethod,
        cash_received_by: saleData.paymentMethod === 'cash' ? saleData.cashReceivedBy : null,
        invoice_number: saleData.invoiceNumber,
        notes: saleData.notes,
        created_at: new Date().toISOString()
      };

      await addSale(newSale);
      setAddSaleOpen(false);
      setSaleData({
        batchId: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        quantityToSell: '',
        pricePerBird: '',
        saleDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash',
        paymentStatus: 'paid',
        cashReceivedBy: '',
        invoiceNumber: '',
        notes: ''
      });
      setError('');
    } catch (error) {
      setError('Failed to create sale. Please try again.');
      console.error('Error creating sale:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSaleData(prev => ({ ...prev, [name]: value }));
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partial': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <Money />;
      case 'mobile_money': return <PhoneAndroid />;
      case 'bank_transfer': return <AccountBalance />;
      case 'pos': return <CreditCard />;
      case 'check': return <BusinessCenter />;
      default: return <Payment />;
    }
  };

  // Filter and search functionality
  const filteredSales = sales.filter(sale => {
    const matchesSearch = searchTerm === '' || 
      (sale.buyer_name || sale.customerName)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sale.buyer_contact || sale.customerPhone)?.includes(searchTerm) ||
      (sale.batch_code || batches.find(b => b.id === sale.batch_id)?.batch_code || '')?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPayment = paymentFilter === 'all' || 
      (sale.payment_status || sale.paymentStatus)?.toLowerCase() === paymentFilter;
    
    const matchesDate = dateFilter === 'all' || (() => {
      const saleDate = new Date(sale.sale_date || sale.saleDate);
      const now = new Date();
      switch (dateFilter) {
        case 'today':
          return saleDate.toDateString() === now.toDateString();
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return saleDate >= weekAgo;
        case 'month':
          return saleDate.getMonth() === now.getMonth() && saleDate.getFullYear() === now.getFullYear();
        case 'quarter':
          const quarter = Math.floor(now.getMonth() / 3);
          const saleQuarter = Math.floor(saleDate.getMonth() / 3);
          return saleQuarter === quarter && saleDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    })();
    
    return matchesSearch && matchesPayment && matchesDate;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportSales = () => {
    const csvContent = [
      ['Date', 'Customer', 'Contact', 'Batch', 'Quantity', 'Price/Unit', 'Total', 'Payment Status', 'Payment Method', 'Cash Received By', 'Invoice'].join(','),
      ...filteredSales.map(sale => [
        new Date(sale.sale_date || sale.saleDate).toLocaleDateString(),
        sale.buyer_name || sale.customerName,
        sale.buyer_contact || sale.customerPhone,
        sale.batch_code || 'N/A',
        sale.quantity_sold || sale.quantityToSell,
        sale.price_per_unit || sale.pricePerBird,
        sale.total_amount || sale.totalAmount,
        sale.payment_status || sale.paymentStatus,
        sale.payment_method || 'cash',
        sale.cash_received_by || 'N/A',
        sale.invoice_number || 'N/A'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Sales Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Sales
                  </Typography>
                  <Typography variant="h4" component="div">
                    {salesStats.totalSales}
                  </Typography>
                </Box>
                <Receipt sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" component="div">
                    {formatCurrency(salesStats.totalRevenue)}
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    This Month
                  </Typography>
                  <Typography variant="h4" component="div">
                    {salesStats.thisMonthSales}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Pending Payments
                  </Typography>
                  <Typography variant="h4" component="div" color="error.main">
                    {salesStats.pendingPayments}
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sales Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="div">
              Sales Records ({filteredSales.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={exportSales}
                disabled={filteredSales.length === 0}
              >
                Export
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddSaleOpen(true)}
              >
                Add Sale
              </Button>
            </Box>
          </Box>

          {/* Search and Filter Controls */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by customer, phone, or batch..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  label="Payment Status"
                  startAdornment={<FilterList sx={{ mr: 1 }} />}
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="quarter">This Quarter</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setPaymentFilter('all');
                  setDateFilter('all');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Price/Unit</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary">
                        No sales records found. Create your first sale!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sales.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell>
                        {new Date(sale.sale_date || sale.saleDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {sale.buyer_name || sale.customerName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {sale.buyer_contact || sale.customerPhone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {sale.batch_code || 'N/A'}
                      </TableCell>
                      <TableCell>{sale.quantity_sold || sale.quantityToSell}</TableCell>
                      <TableCell>{formatCurrency(sale.price_per_unit || sale.pricePerBird)}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {formatCurrency(sale.total_amount || sale.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getPaymentMethodIcon(sale.payment_method || 'cash')}
                          <Chip
                            label={sale.payment_status || sale.paymentStatus}
                            color={getPaymentStatusColor(sale.payment_status || sale.paymentStatus)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedSale(sale);
                              setViewSaleOpen(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Sale Dialog */}
      <Dialog open={addSaleOpen} onClose={() => setAddSaleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Sale</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Select Batch</InputLabel>
                <Select
                  name="batchId"
                  value={saleData.batchId}
                  onChange={handleChange}
                  label="Select Batch"
                >
                  {batches.filter(batch => batch.status === 'Active').map((batch) => (
                    <MenuItem key={batch.id} value={batch.id}>
                      {batch.batch_code} - {batch.type} ({batch.current_count || batch.count} birds)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="saleDate"
                label="Sale Date"
                type="date"
                value={saleData.saleDate}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="customerName"
                label="Customer Name"
                value={saleData.customerName}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="customerPhone"
                label="Customer Phone"
                value={saleData.customerPhone}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="quantityToSell"
                label="Quantity to Sell"
                type="number"
                value={saleData.quantityToSell}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="pricePerBird"
                label="Price per Bird (TZS)"
                type="number"
                value={saleData.pricePerBird}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  name="paymentMethod"
                  value={saleData.paymentMethod}
                  onChange={handleChange}
                  label="Payment Method"
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="mobile_money">Mobile Money</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="pos">POS Transfer</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  name="paymentStatus"
                  value={saleData.paymentStatus}
                  onChange={handleChange}
                  label="Payment Status"
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {saleData.paymentMethod === 'cash' && (
              <Grid item xs={12} md={6}>
                <TextField
                  name="cashReceivedBy"
                  label="Cash Received By"
                  value={saleData.cashReceivedBy}
                  onChange={handleChange}
                  fullWidth
                  required
                  placeholder="Name of person who received cash"
                />
              </Grid>
            )}
            
            <Grid item xs={12} md={saleData.paymentMethod === 'cash' ? 6 : 12}>
              <TextField
                name="invoiceNumber"
                label="Invoice Number"
                value={saleData.invoiceNumber}
                onChange={handleChange}
                fullWidth
                placeholder="Auto-generated"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={saleData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            {saleData.quantityToSell && saleData.pricePerBird && (
              <Grid item xs={12}>
                <Typography variant="h6" color="primary">
                  Total Amount: {formatCurrency(parseFloat(saleData.quantityToSell) * parseFloat(saleData.pricePerBird))}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSaleOpen(false)}>Cancel</Button>
          <Button onClick={handleAddSale} variant="contained">
            Create Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced View Sale Dialog */}
      <Dialog open={viewSaleOpen} onClose={() => setViewSaleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Receipt />
          Sale Details
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedSale && (
            <Box>
              {/* Header Section */}
              <Box sx={{ bgcolor: 'grey.50', p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      {selectedSale.buyer_name || selectedSale.customerName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">
                        {selectedSale.buyer_contact || selectedSale.customerPhone}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2">
                        {new Date(selectedSale.sale_date || selectedSale.saleDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {formatCurrency(selectedSale.total_amount || selectedSale.totalAmount)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Invoice: {selectedSale.invoice_number || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Main Content */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Sale Information */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Receipt color="primary" />
                      Sale Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><BusinessCenter fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary="Batch" 
                          secondary={selectedSale.batch_code || 'N/A'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Assessment fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary="Quantity" 
                          secondary={`${selectedSale.quantity_sold || selectedSale.quantityToSell} birds`} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AttachMoney fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary="Price per Bird" 
                          secondary={formatCurrency(selectedSale.price_per_unit || selectedSale.pricePerBird)} 
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  {/* Payment Information */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Payment color="primary" />
                      Payment Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          {getPaymentMethodIcon(selectedSale.payment_method || 'cash')}
                        </ListItemIcon>
                        <ListItemText 
                          primary="Payment Method" 
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {(selectedSale.payment_method || 'cash').replace('_', ' ')}
                              </Typography>
                            </Box>
                          } 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {selectedSale.payment_status === 'paid' ? <CheckCircle color="success" fontSize="small" /> : <Schedule color="warning" fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary="Payment Status" 
                          secondary={
                            <Chip
                              label={selectedSale.payment_status || selectedSale.paymentStatus}
                              color={getPaymentStatusColor(selectedSale.payment_status || selectedSale.paymentStatus)}
                              size="small"
                            />
                          } 
                        />
                      </ListItem>
                      {(selectedSale.payment_method === 'cash' || !selectedSale.payment_method) && selectedSale.cash_received_by && (
                        <ListItem>
                          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                          <ListItemText 
                            primary="Cash Received By" 
                            secondary={selectedSale.cash_received_by} 
                          />
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                </Grid>

                {selectedSale.notes && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Notes
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2">
                        {selectedSale.notes}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSaleOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesManagement;
